/**
 * Content Table Component for v0.7.0
 * Displays and manages ads/wishes for admin
 */

import { useState, useEffect } from 'react';
import { EditAdModal, EditWishModal } from '../EditModal';
import './ContentTable.css';

const API_BASE = (import.meta.env as { VITE_API_URL?: string }).VITE_API_URL || 'http://localhost:8787';

export type ContentType = 'ads' | 'wishes';

interface ContentTableProps {
  type: ContentType;
  status?: string;
  onEdit?: (id: string) => void;
}

interface ContentItem {
  id: string;
  title: string;
  category: string;
  region: string;
  status: string;
  viewCount: number;
  createdAt: string;
  storeName?: string;
  budget?: string | null;
  urgent?: boolean;
}

const ContentTable = ({ type, status }: ContentTableProps) => {
  const [items, setItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<ContentItem | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Row-level state tracking for operations
  const [operatingIds, setOperatingIds] = useState<Set<string>>(new Set());
  const [pendingDeletes, setPendingDeletes] = useState<Set<string>>(new Set());
  
  const limit = 20;

  useEffect(() => {
    loadContent();
  }, [type, status, page]);

  // Helper functions to manage operation states
  const addToOperating = (id: string) => {
    setOperatingIds(prev => new Set([...prev, id]));
  };

  const removeFromOperating = (id: string) => {
    setOperatingIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const addToPendingDeletes = (...ids: string[]) => {
    setPendingDeletes(prev => new Set([...prev, ...ids]));
  };

  const isOperating = (id: string) => operatingIds.has(id);
  const isPendingDelete = (id: string) => pendingDeletes.has(id);

  const getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };

  const loadContent = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (page * limit).toString(),
      });
      if (status) params.append('status', status);

      // 添加時間戳以避免瀏覽器快取
      params.append('_t', Date.now().toString());

      const response = await fetch(`${API_BASE}/api/admin/content/${type}?${params}`, {
        headers: getAuthHeaders(),
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setItems(data.data);
        setTotal(data.pagination?.total || 0);
      } else {
        throw new Error(data.error?.message || '載入資料失敗');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : '載入資料時發生錯誤';
      console.error('Error loading content:', err);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((item) => item.id)));
    }
  };

  const handleSelectOne = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    const item = items.find(i => i.id === id);
    if (!item) return;

    const oldStatus = item.status;
    
    // Optimistic update: immediately update local state
    setItems(prev => prev.map(i => 
      i.id === id ? { ...i, status: newStatus } : i
    ));
    addToOperating(id);

    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/content/${type}/${id}/status`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `HTTP ${response.status}: 更新狀態失敗`);
      }
    } catch (err) {
      // Revert on error
      setItems(prev => prev.map(i => 
        i.id === id ? { ...i, status: oldStatus } : i
      ));
      const message = err instanceof Error ? err.message : '更新狀態時發生錯誤';
      setError(message);
      alert(`更新失敗: ${message}`);
    } finally {
      removeFromOperating(id);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(`確定要刪除此${type === 'ads' ? '廣告' : '許願'}嗎？`)) return;

    addToOperating(id);
    addToPendingDeletes(id);

    // Wait for fade-out animation
    await new Promise(resolve => setTimeout(resolve, 300));

    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/content/${type}/${id}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok && response.status !== 204) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `HTTP ${response.status}: 刪除失敗`);
      }

      // Remove from local list
      setItems(prev => prev.filter(i => i.id !== id));
      setTotal(prev => Math.max(0, prev - 1));
    } catch (err) {
      // Cancel fade-out on error
      setPendingDeletes(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      const message = err instanceof Error ? err.message : '刪除時發生錯誤';
      setError(message);
      alert(`刪除失敗: ${message}`);
    } finally {
      removeFromOperating(id);
    }
  };

  const handleEdit = (item: ContentItem) => {
    setEditingItem(item);
    setShowEditModal(true);
  };

  const handleEditSave = (updatedItem?: Partial<ContentItem> & { id: string }) => {
    setShowEditModal(false);
    setEditingItem(null);
    
    if (updatedItem) {
      // Local update: only update the edited row data
      setItems(prev => prev.map(i => 
        i.id === updatedItem.id ? { ...i, ...updatedItem } : i
      ));
    }
  };

  const handleBatchStatus = async (newStatus: string) => {
    const idsToUpdate = Array.from(selectedIds);
    const oldStatuses = new Map(idsToUpdate.map(id => [id, items.find(i => i.id === id)?.status]));
    
    // Optimistic update: immediately update local status
    setItems(prev => prev.map(i => 
      selectedIds.has(i.id) ? { ...i, status: newStatus } : i
    ));
    
    // Add all IDs to operating state
    setOperatingIds(prev => new Set([...prev, ...idsToUpdate]));
    setSelectedIds(new Set());

    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/content/batch/status`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ids: idsToUpdate,
          type: type === 'ads' ? 'ad' : 'wish',
          status: newStatus,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `HTTP ${response.status}: 批次更新失敗`);
      }

      const data = await response.json();
      alert(`成功更新 ${data.data?.count || idsToUpdate.length} 項`);
    } catch (err) {
      // Rollback all statuses on error
      setItems(prev => prev.map(i => {
        const oldStatus = oldStatuses.get(i.id);
        return oldStatus && i.id === oldStatus[0] ? { ...i, status: oldStatus[1] } : i;
      }));
      const message = err instanceof Error ? err.message : '批次更新時發生錯誤';
      setError(message);
      alert(`批次更新失敗: ${message}`);
    } finally {
      // Remove all operating IDs
      setOperatingIds(prev => {
        const next = new Set(prev);
        idsToUpdate.forEach(id => next.delete(id));
        return next;
      });
    }
  };

  const handleBatchDelete = async () => {
    const idsToDelete = Array.from(selectedIds);
    if (!confirm(`確定要刪除選中的 ${idsToDelete.length} 項嗎？`)) return;

    // Add all IDs to operating state
    setOperatingIds(prev => new Set([...prev, ...idsToDelete]));
    addToPendingDeletes(...idsToDelete);

    setSelectedIds(new Set());

    // Wait for fade-out animation
    await new Promise(resolve => setTimeout(resolve, 300));

    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/admin/content/batch/delete`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ids: idsToDelete,
          type: type === 'ads' ? 'ad' : 'wish',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `HTTP ${response.status}: 批次刪除失敗`);
      }

      const data = await response.json();
      // Remove from local list
      setItems(prev => prev.filter(i => !idsToDelete.includes(i.id)));
      setTotal(prev => Math.max(0, prev - (data.data?.count || idsToDelete.length)));
      alert(`成功刪除 ${data.data?.count || idsToDelete.length} 項`);
    } catch (err) {
      // Cancel fade-out on error
      setPendingDeletes(prev => {
        const next = new Set(prev);
        idsToDelete.forEach(id => next.delete(id));
        return next;
      });
      const message = err instanceof Error ? err.message : '批次刪除時發生錯誤';
      setError(message);
      alert(`批次刪除失敗: ${message}`);
    } finally {
      // Remove all operating IDs
      setOperatingIds(prev => {
        const next = new Set(prev);
        idsToDelete.forEach(id => next.delete(id));
        return next;
      });
    }
  };

  const getStatusClass = (itemStatus: string) => {
    const statusMap: Record<string, string> = {
      active: 'content-table__status--active',
      inactive: 'content-table__status--inactive',
      pending: 'content-table__status--pending',
      rejected: 'content-table__status--rejected',
      open: 'content-table__status--open',
      matched: 'content-table__status--matched',
      closed: 'content-table__status--closed',
    };
    return statusMap[itemStatus] || '';
  };

  const getStatusLabel = (itemStatus: string) => {
    const labelMap: Record<string, string> = {
      active: '上架中',
      inactive: '已下架',
      pending: '待審核',
      rejected: '未通過',
      open: '進行中',
      matched: '已配對',
      closed: '已關閉',
    };
    return labelMap[itemStatus] || itemStatus;
  };

  if (loading) {
    return <div className="content-table__loading">載入中...</div>;
  }

  if (error && items.length === 0) {
    return (
      <div className="content-table">
        <div className="content-table__error">
          <p>載入失敗: {error}</p>
          <button onClick={loadContent} className="btn btn--sm">
            重新載入
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="content-table">
      <div className="content-table__header">
        <h2 className="content-table__title">
          {type === 'ads' ? '廣告管理' : '許願管理'}
          {total > 0 && ` (${total})`}
        </h2>
        <div className="content-table__actions">
          {selectedIds.size > 0 && (
            <>
              <button
                onClick={() => handleBatchStatus(type === 'ads' ? 'inactive' : 'closed')}
                className="btn btn--sm"
                disabled={Array.from(selectedIds).some(id => operatingIds.has(id))}
              >
                批次下架
              </button>
              <button
                onClick={handleBatchDelete}
                className="btn btn--sm btn--danger"
                disabled={Array.from(selectedIds).some(id => operatingIds.has(id))}
              >
                批次刪除
              </button>
            </>
          )}
        </div>
      </div>

      {error && items.length > 0 && (
        <div className="content-table__error-bar">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)} className="btn btn--sm">
            關閉
          </button>
        </div>
      )}

      {selectedIds.size > 0 && (
        <div className="content-table__bulk-bar">
          <span className="content-table__bulk-count">
            已選取 {selectedIds.size} 項
          </span>
          <button onClick={() => setSelectedIds(new Set())} className="btn btn--sm">
            取消選取
          </button>
        </div>
      )}

      <div className="content-table__table-wrapper">
        <table className="content-table__table">
          <thead className="content-table__thead">
            <tr>
              <th className="content-table__th" style={{ width: '40px' }}>
                <input
                  type="checkbox"
                  checked={selectedIds.size === items.length && items.length > 0}
                  onChange={handleSelectAll}
                  className="content-table__checkbox"
                />
              </th>
              <th className="content-table__th">標題</th>
              <th className="content-table__th">類別</th>
              <th className="content-table__th">地區</th>
              {type === 'ads' && <th className="content-table__th">商店</th>}
              {type === 'wishes' && <th className="content-table__th">預算</th>}
              {type === 'wishes' && <th className="content-table__th">緊急</th>}
              <th className="content-table__th">狀態</th>
              <th className="content-table__th">瀏覽</th>
              <th className="content-table__th">建立時間</th>
              <th className="content-table__th content-table__th--actions">操作</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={10} className="content-table__empty">
                  沒有資料
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr 
                  key={item.id} 
                  className={`content-table__row ${
                    isPendingDelete(item.id) ? 'content-table__row--deleting' : ''
                  } ${
                    isOperating(item.id) ? 'content-table__row--operating' : ''
                  }`}
                >
                  <td className="content-table__td">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(item.id)}
                      onChange={() => handleSelectOne(item.id)}
                      className="content-table__checkbox"
                    />
                  </td>
                  <td className="content-table__td">
                    <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.title}
                    </div>
                  </td>
                  <td className="content-table__td">{item.category}</td>
                  <td className="content-table__td">{item.region}</td>
                  {type === 'ads' && (
                    <td className="content-table__td">
                      {item.storeName || '-'}
                    </td>
                  )}
                  {type === 'wishes' && (
                    <td className="content-table__td">
                      {item.budget || '-'}
                    </td>
                  )}
                  {type === 'wishes' && (
                    <td className="content-table__td">
                      {item.urgent && (
                        <span className="content-table__urgent">!</span>
                      )}
                    </td>
                  )}
                  <td className="content-table__td">
                    <span className={`content-table__status ${getStatusClass(item.status)}`}>
                      {getStatusLabel(item.status)}
                    </span>
                  </td>
                  <td className="content-table__td">{item.viewCount}</td>
                  <td className="content-table__td">
                    {new Date(item.createdAt).toLocaleDateString('zh-TW')}
                  </td>
                  <td className="content-table__td">
                    <div className="content-table__actions">
                      {isOperating(item.id) ? (
                        <span className="content-table__cell-loading">處理中</span>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEdit(item)}
                            className="btn btn--sm btn--primary"
                            title="編輯"
                          >
                            編輯
                          </button>
                          <select
                            value={item.status}
                            onChange={(e) => handleStatusChange(item.id, e.target.value)}
                            className="content-table__status-select"
                          >
                            {type === 'ads' ? (
                              <>
                                <option value="active">上架</option>
                                <option value="inactive">下架</option>
                                <option value="pending">待審</option>
                              </>
                            ) : (
                              <>
                                <option value="open">進行中</option>
                                <option value="matched">已配對</option>
                                <option value="closed">已關閉</option>
                              </>
                            )}
                          </select>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="btn btn--sm btn--danger content-table__delete-btn"
                            title="刪除"
                          >
                            刪除
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {total > limit && (
        <div className="content-table__pagination">
          <div className="content-table__page-info">
            顯示 {page * limit + 1} - {Math.min((page + 1) * limit, total)} / 共 {total} 項
          </div>
          <div className="content-table__page-controls">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="btn btn--sm"
            >
              上一頁
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={(page + 1) * limit >= total}
              className="btn btn--sm"
            >
              下一頁
            </button>
          </div>
        </div>
      )}

      {/* Edit Modals */}
      {type === 'ads' && (
        <EditAdModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingItem(null);
          }}
          ad={editingItem}
          onSave={handleEditSave}
        />
      )}

      {type === 'wishes' && (
        <EditWishModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingItem(null);
          }}
          wish={editingItem}
          onSave={handleEditSave}
        />
      )}
    </div>
  );
};

export default ContentTable;
